import { useEffect, useState, type CSSProperties } from "react";
import {
  listClubPours,
  snapshotClubPoursNow,
  upsertClubPourMonth,
  deleteClubPourMonth,
  searchClubPourBottles,
  saveClubPourLineup,
  type ClubPourMonth,
  type SearchBottleItem,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";

export default function AdminClubPours() {
  const [months, setMonths] = useState<ClubPourMonth[]>([]);
  const [currentMonthStr, setCurrentMonthStr] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [snapshotting, setSnapshotting] = useState(false);

  // Add Month Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMonthYear, setNewMonthYear] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creatingMonth, setCreatingMonth] = useState(false);

  // Edit Meta Modal state
  const [editMetaMonth, setEditMetaMonth] = useState<ClubPourMonth | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // Bottle Planner Modal state (Future months)
  const [plannerMonth, setPlannerMonth] = useState<ClubPourMonth | null>(null);
  const [bottleQuery, setBottleQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchBottleItem[]>([]);
  const [searchingBottles, setSearchingBottles] = useState(false);
  const [selectedBottles, setSelectedBottles] = useState<
    { menu_item_id: string; name: string; feature_notes: string }[]
  >([]);
  const [savingLineup, setSavingLineup] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await listClubPours();
      setCurrentMonthStr(res.current_month || "");
      setMonths(res.months || []);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load club pour months");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const handleSnapshot = async () => {
    setSnapshotting(true);
    setErrorMsg(null);
    try {
      const res = await snapshotClubPoursNow();
      const r = res.result || {};
      showToast(
        `Snapshot saved for current month (+${r.inserted || 0} added, −${r.deleted || 0} removed).`
      );
      await loadData();
    } catch (err: any) {
      setErrorMsg("Snapshot failed: " + (err?.message || "Error"));
    } finally {
      setSnapshotting(false);
    }
  };

  const handleCreateMonth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMonthYear) return;
    setCreatingMonth(true);
    try {
      await upsertClubPourMonth({
        month_year: newMonthYear,
        title: newTitle.trim() || null,
        description: newDescription.trim() || null,
      });
      setShowAddForm(false);
      setNewMonthYear("");
      setNewTitle("");
      setNewDescription("");
      showToast(`Added upcoming month ${newMonthYear}.`);
      await loadData();
    } catch (err: any) {
      setErrorMsg("Could not create month: " + (err?.message || "Error"));
    } finally {
      setCreatingMonth(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!editMetaMonth) return;
    setSavingMeta(true);
    try {
      await upsertClubPourMonth({
        month_year: editMetaMonth.month_year,
        title: editTitle.trim() || null,
        description: editDesc.trim() || null,
      });
      setEditMetaMonth(null);
      showToast("Theme and notes updated.");
      await loadData();
    } catch (err: any) {
      setErrorMsg("Could not save: " + (err?.message || "Error"));
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDeleteMonth = async (monthYear: string) => {
    if (
      !window.confirm(
        `Delete the planned month ${monthYear} and its bottle lineup? This cannot be undone.`
      )
    )
      return;
    try {
      await deleteClubPourMonth(monthYear);
      showToast(`Deleted ${monthYear}.`);
      await loadData();
    } catch (err: any) {
      setErrorMsg("Could not delete: " + (err?.message || "Error"));
    }
  };

  // Open Planner Modal
  const handleOpenPlanner = (m: ClubPourMonth) => {
    setPlannerMonth(m);
    setBottleQuery("");
    setSearchResults([]);
    const curFeatures = (m.collective_33_features || [])
      .slice()
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map((f) => ({
        menu_item_id: f.menu_item_id,
        name: f.menu_items?.name || f.bottle_name_snapshot || "Bottle",
        feature_notes: f.feature_notes || "",
      }));
    setSelectedBottles(curFeatures);
  };

  // Search bottles debounce
  useEffect(() => {
    if (!plannerMonth || !bottleQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingBottles(true);
      try {
        const res = await searchClubPourBottles(bottleQuery.trim());
        setSearchResults(res.bottles || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchingBottles(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [bottleQuery, plannerMonth]);

  const handleAddBottleToPlanner = (bottle: SearchBottleItem) => {
    if (selectedBottles.some((b) => b.menu_item_id === bottle.id)) return;
    setSelectedBottles((prev) => [
      ...prev,
      { menu_item_id: bottle.id, name: bottle.name, feature_notes: "" },
    ]);
  };

  const handleRemoveBottleFromPlanner = (id: string) => {
    setSelectedBottles((prev) => prev.filter((b) => b.menu_item_id !== id));
  };

  const handleUpdateBottleNote = (id: string, note: string) => {
    setSelectedBottles((prev) =>
      prev.map((b) => (b.menu_item_id === id ? { ...b, feature_notes: note } : b))
    );
  };

  const handleSaveLineup = async () => {
    if (!plannerMonth) return;
    setSavingLineup(true);
    try {
      await saveClubPourLineup({
        month_year: plannerMonth.month_year,
        items: selectedBottles.map((b, idx) => ({
          menu_item_id: b.menu_item_id,
          feature_notes: b.feature_notes.trim() || null,
          display_order: idx,
        })),
      });
      setPlannerMonth(null);
      showToast(`Lineup saved for ${plannerMonth.month_year}.`);
      await loadData();
    } catch (err: any) {
      setErrorMsg("Failed to save lineup: " + (err?.message || "Error"));
    } finally {
      setSavingLineup(false);
    }
  };

  const fmtMonthLabel = (my: string) => {
    if (!my) return "—";
    try {
      const [y, m] = my.split("-").map(Number);
      return new Date(y, m - 1, 1).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    } catch {
      return my;
    }
  };

  const getMonthState = (my: string) => {
    if (my === currentMonthStr) return "current";
    return my < currentMonthStr ? "past" : "future";
  };

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="pours" badgeCounts={{ pours: months.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Club Pour Planner</h1>
          <p style={styles.subtitle}>
            Collective 33 monthly pour history and upcoming lineup planning
          </p>
        </div>
        <div style={styles.headerActions}>
          <button
            style={styles.snapshotBtn}
            onClick={handleSnapshot}
            disabled={snapshotting}
          >
            {snapshotting ? "Snapshotting..." : "📸 Snapshot Current Month"}
          </button>
          <button
            style={styles.actionBtn}
            onClick={() => setShowAddForm((prev) => !prev)}
          >
            {showAddForm ? "Cancel" : "+ Add Upcoming Month"}
          </button>
          <button style={styles.refreshBtn} onClick={loadData} disabled={loading}>
            {loading ? "..." : "↻"}
          </button>
        </div>
      </div>

      {/* Messages */}
      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}
      {successMsg && <div style={styles.successBanner}>{successMsg}</div>}

      {/* Intro Note */}
      <div style={styles.introCard}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          The <strong>current month mirrors Square</strong> (curate the lineup in Square under the
          <em> “SR Collective 33 Monthly Pours”</em> category, then hit <em>Snapshot current month</em>).
          <strong> Past months are frozen</strong> historical archives. <strong>Future months</strong> can be
          hand-planned and saved in advance here.
        </p>
      </div>

      {/* Add Upcoming Month Form */}
      {showAddForm && (
        <form style={styles.addForm} onSubmit={handleCreateMonth}>
          <div style={styles.formRow}>
            <div style={{ flex: 1 }}>
              <label style={styles.fieldLabel}>Month</label>
              <input
                type="month"
                style={styles.input}
                required
                value={newMonthYear}
                onChange={(e) => setNewMonthYear(e.target.value)}
              />
            </div>
            <div style={{ flex: 2 }}>
              <label style={styles.fieldLabel}>Theme / Title (Optional)</label>
              <input
                type="text"
                style={styles.input}
                placeholder="e.g. Peated Scotch Showcase"
                maxLength={120}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={styles.fieldLabel}>Planning Notes (Optional)</label>
            <input
              type="text"
              style={styles.input}
              placeholder="e.g. Focus on cask strength releases and limited allocations"
              maxLength={500}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
            <button style={styles.saveBtn} type="submit" disabled={creatingMonth}>
              {creatingMonth ? "Creating..." : "Create Month"}
            </button>
            <button
              style={styles.cancelBtn}
              type="button"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Months Timeline */}
      <div style={styles.timeline}>
        {loading ? (
          <div style={styles.empty}>Loading club pour schedule...</div>
        ) : months.length === 0 ? (
          <div style={styles.empty}>No months logged yet. Add an upcoming month or take a snapshot.</div>
        ) : (
          months.map((m) => {
            const st = getMonthState(m.month_year);
            const feats = (m.collective_33_features || [])
              .slice()
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

            const isCurrent = st === "current";
            const isPast = st === "past";
            const isFuture = st === "future";

            return (
              <div
                key={m.month_year}
                style={{
                  ...styles.monthCard,
                  ...(isCurrent ? styles.currentCard : {}),
                }}
              >
                <div style={styles.monthHeader}>
                  <div>
                    <span style={styles.monthName}>{fmtMonthLabel(m.month_year)}</span>
                    <span
                      style={{
                        ...styles.badge,
                        ...(isCurrent
                          ? styles.badgeCurrent
                          : isPast
                          ? styles.badgePast
                          : styles.badgeFuture),
                      }}
                    >
                      {isCurrent
                        ? "Live — Synced from Square"
                        : isPast
                        ? "Archived"
                        : "Planned"}
                    </span>
                  </div>
                  <span style={styles.bottleCount}>
                    {feats.length} {feats.length === 1 ? "bottle" : "bottles"}
                  </span>
                </div>

                {/* Theme & Notes */}
                {(m.title || m.description) && (
                  <div style={styles.metaBox}>
                    {m.title && <div style={styles.metaTitle}>🌟 {m.title}</div>}
                    {m.description && <div style={styles.metaDesc}>{m.description}</div>}
                  </div>
                )}

                {/* Featured Bottles List */}
                <div style={styles.bottleList}>
                  {feats.length === 0 ? (
                    <div style={styles.noBottles}>
                      {isFuture ? "No bottles planned yet." : "No bottles recorded."}
                    </div>
                  ) : (
                    feats.map((f) => {
                      const bottleName =
                        f.menu_items?.name || f.bottle_name_snapshot || "(removed bottle)";
                      return (
                        <div key={f.id} style={styles.bottleItem}>
                          <div style={styles.bottleNameRow}>
                            <span style={styles.bottleName}>🥃 {bottleName}</span>
                            {f.source === "manual" && (
                              <span style={styles.manualTag}>planned</span>
                            )}
                          </div>
                          {f.feature_notes && (
                            <div style={styles.bottleNote}>{f.feature_notes}</div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Actions Footer */}
                <div style={styles.cardActions}>
                  {isCurrent && (
                    <>
                      <span style={styles.hint}>
                        Curate in Square under C33 Monthly Pours, then Snapshot.
                      </span>
                      <button
                        style={styles.actionBtnSmall}
                        onClick={() => {
                          setEditMetaMonth(m);
                          setEditTitle(m.title || "");
                          setEditDesc(m.description || "");
                        }}
                      >
                        Edit Theme / Notes
                      </button>
                    </>
                  )}
                  {isFuture && (
                    <>
                      <button
                        style={styles.goldBtnSmall}
                        onClick={() => handleOpenPlanner(m)}
                      >
                        Plan Bottles
                      </button>
                      <button
                        style={styles.actionBtnSmall}
                        onClick={() => {
                          setEditMetaMonth(m);
                          setEditTitle(m.title || "");
                          setEditDesc(m.description || "");
                        }}
                      >
                        Edit Theme / Notes
                      </button>
                      <button
                        style={styles.deleteBtnSmall}
                        onClick={() => handleDeleteMonth(m.month_year)}
                      >
                        Delete Month
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Edit Meta Modal */}
      {editMetaMonth && (
        <div style={styles.modalOverlay} onClick={() => setEditMetaMonth(null)}>
          <div style={styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              Theme & Notes — {fmtMonthLabel(editMetaMonth.month_year)}
            </h2>
            <div style={{ marginTop: 14 }}>
              <label style={styles.fieldLabel}>Theme / Title</label>
              <input
                type="text"
                style={styles.input}
                maxLength={120}
                placeholder="e.g. Bourbon Heritage Month"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={styles.fieldLabel}>Planning Notes</label>
              <textarea
                style={{ ...styles.input, height: 80, resize: "vertical" }}
                maxLength={500}
                placeholder="Notes for staff or marketing description"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
              />
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                style={styles.cancelBtn}
                onClick={() => setEditMetaMonth(null)}
              >
                Cancel
              </button>
              <button
                style={styles.saveBtn}
                disabled={savingMeta}
                onClick={handleSaveMeta}
              >
                {savingMeta ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottle Lineup Planner Modal (Future Months) */}
      {plannerMonth && (
        <div style={styles.modalOverlay} onClick={() => setPlannerMonth(null)}>
          <div style={{ ...styles.modalBody, width: 700 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>
                  Plan Lineup — {fmtMonthLabel(plannerMonth.month_year)}
                </h2>
                <p style={styles.modalMeta}>
                  Search bottles in catalog and build this month's featured list
                </p>
              </div>
              <button style={styles.closeBtn} onClick={() => setPlannerMonth(null)}>
                ✕
              </button>
            </div>

            <div style={styles.plannerGrid}>
              {/* Left Column: Search & Results */}
              <div style={styles.searchCol}>
                <label style={styles.fieldLabel}>Search Bottles</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Search bottle name..."
                  value={bottleQuery}
                  onChange={(e) => setBottleQuery(e.target.value)}
                  autoFocus
                />
                <div style={styles.resultsList}>
                  {searchingBottles ? (
                    <div style={styles.pickerStatus}>Searching...</div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((b) => {
                      const isSelected = selectedBottles.some(
                        (sb) => sb.menu_item_id === b.id
                      );
                      return (
                        <div
                          key={b.id}
                          style={{
                            ...styles.resultRow,
                            ...(isSelected ? styles.resultRowSelected : {}),
                          }}
                          onClick={() => handleAddBottleToPlanner(b)}
                        >
                          <span style={{ fontSize: 13, color: "var(--text)" }}>{b.name}</span>
                          {isSelected ? (
                            <span style={{ color: "var(--green, #27ae60)", fontWeight: 700 }}>
                              ✓ Added
                            </span>
                          ) : (
                            <span style={{ color: "var(--gold, #F9AF36)", fontSize: 12 }}>
                              + Add
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : bottleQuery.trim() ? (
                    <div style={styles.pickerStatus}>No matching bottles found.</div>
                  ) : (
                    <div style={styles.pickerStatus}>Type to search the bottle library.</div>
                  )}
                </div>
              </div>

              {/* Right Column: Selected Lineup */}
              <div style={styles.selectedCol}>
                <label style={styles.fieldLabel}>
                  Selected Lineup ({selectedBottles.length})
                </label>
                <div style={styles.selectedList}>
                  {selectedBottles.length === 0 ? (
                    <div style={styles.pickerStatus}>
                      No bottles selected yet. Click from search results to add.
                    </div>
                  ) : (
                    selectedBottles.map((b, idx) => (
                      <div key={b.menu_item_id} style={styles.selectedRow}>
                        <div style={styles.selectedTop}>
                          <span style={styles.selectedName}>
                            {idx + 1}. {b.name}
                          </span>
                          <button
                            style={styles.removeBtn}
                            onClick={() => handleRemoveBottleFromPlanner(b.menu_item_id)}
                          >
                            ✕
                          </button>
                        </div>
                        <input
                          type="text"
                          style={styles.noteInput}
                          placeholder="Feature notes (e.g. 10yr bourbon, peated)"
                          value={b.feature_notes}
                          onChange={(e) =>
                            handleUpdateBottleNote(b.menu_item_id, e.target.value)
                          }
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                style={styles.cancelBtn}
                onClick={() => setPlannerMonth(null)}
              >
                Cancel
              </button>
              <button
                style={styles.saveBtn}
                disabled={savingLineup}
                onClick={handleSaveLineup}
              >
                {savingLineup ? "Saving Lineup..." : "Save Lineup"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  snapshotBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  actionBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  refreshBtn: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  introCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "14px 16px",
    color: "var(--muted, #8A7A66)",
    marginBottom: 20,
  },
  addForm: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--gold, #F9AF36)",
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  formRow: {
    display: "flex",
    gap: 12,
  },
  fieldLabel: {
    display: "block",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--muted, #8A7A66)",
    marginBottom: 4,
  },
  input: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
  },
  timeline: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  monthCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "16px 18px",
  },
  currentCard: {
    borderColor: "rgba(249, 175, 54, 0.5)",
    boxShadow: "0 0 12px rgba(249, 175, 54, 0.08)",
  },
  monthHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    flexWrap: "wrap",
    gap: 8,
  },
  monthName: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
    marginRight: 10,
  },
  badge: {
    padding: "3px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  badgeCurrent: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
  },
  badgePast: {
    background: "rgba(138, 122, 102, 0.12)",
    color: "var(--muted, #8A7A66)",
  },
  badgeFuture: {
    background: "rgba(52, 152, 219, 0.15)",
    color: "#60a5fa",
  },
  bottleCount: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
  },
  metaBox: {
    background: "rgba(0,0,0,0.2)",
    padding: "8px 12px",
    borderRadius: 6,
    marginBottom: 12,
  },
  metaTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    marginBottom: 2,
  },
  metaDesc: {
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
  },
  bottleList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 12,
  },
  bottleItem: {
    background: "rgba(0,0,0,0.15)",
    border: "1px solid rgba(255,255,255,0.03)",
    borderRadius: 6,
    padding: "8px 12px",
  },
  bottleNameRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  bottleName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text, #F0E6D4)",
  },
  manualTag: {
    fontSize: 10,
    color: "#60a5fa",
    background: "rgba(96, 165, 250, 0.1)",
    padding: "2px 6px",
    borderRadius: 3,
    textTransform: "uppercase",
  },
  bottleNote: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 3,
  },
  noBottles: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    fontStyle: "italic",
    padding: "6px 0",
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    borderTop: "1px solid var(--border, #2A1F14)",
    paddingTop: 10,
    marginTop: 6,
  },
  hint: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginRight: "auto",
  },
  actionBtnSmall: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 4,
    padding: "5px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  goldBtnSmall: {
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#111",
    borderRadius: 4,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  deleteBtnSmall: {
    background: "transparent",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    color: "#ff7666",
    borderRadius: 4,
    padding: "5px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
    background: "var(--surface, #13100B)",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalBody: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    width: 500,
    maxWidth: "95vw",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: 24,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 12,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  modalMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
  },
  plannerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 16,
  },
  searchCol: {},
  resultsList: {
    marginTop: 8,
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    maxHeight: 250,
    overflowY: "auto",
    background: "rgba(0,0,0,0.2)",
  },
  resultRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 10px",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
    cursor: "pointer",
  },
  resultRowSelected: {
    background: "rgba(39, 174, 96, 0.1)",
  },
  selectedCol: {},
  selectedList: {
    marginTop: 8,
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    maxHeight: 250,
    overflowY: "auto",
    background: "rgba(0,0,0,0.2)",
    padding: 6,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  selectedRow: {
    background: "rgba(255,255,255,0.03)",
    borderRadius: 5,
    padding: "8px 10px",
    border: "1px solid var(--border, #2A1F14)",
  },
  selectedTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  selectedName: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text, #F0E6D4)",
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
    fontSize: 12,
  },
  noteInput: {
    width: "100%",
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 11,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  pickerStatus: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    textAlign: "center",
    padding: 16,
    fontStyle: "italic",
  },
  redeemBtn: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid #27ae60",
    color: "#4ade80",
    borderRadius: 5,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  memberResultsList: {
    maxHeight: 180,
    overflowY: "auto",
    background: "rgba(0,0,0,0.3)",
    borderRadius: 6,
    border: "1px solid var(--border, #2A1F14)",
    marginBottom: 12,
  },
  memberResultItem: {
    padding: "8px 12px",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    cursor: "pointer",
    transition: "background 0.1s ease",
  },
  selectedMemberBox: {
    background: "rgba(249, 175, 54, 0.1)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    padding: "10px 12px",
    borderRadius: 6,
    marginBottom: 12,
  },
  select: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 10px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
};
